import { X } from 'lucide-react';
import type { CSSProperties } from 'react';

/**
 * CloseButton — the shared "dismiss" affordance for modals, drawers, and
 * popovers (BON-203 round 3 Q2). Centralizes what used to be a divergent bare
 * "×" glyph across surfaces: an icon button with a VISIBLE border + adequate
 * hit-area (28×28) so it reads as a real control, not stray punctuation.
 *
 * Two-tier: this is the shared primitive; each surface keeps its own placement
 * + handler. Always exposes `aria-label` (default "Close") so role/label
 * queries resolve consistently.
 */
interface CloseButtonProps {
  onClick: () => void;
  /** Accessible name. Default "Close". */
  ariaLabel?: string;
  /** Icon size in px. Default 16. */
  size?: number;
  /** Merged onto the button (e.g. a header-specific color/margin). */
  style?: CSSProperties;
  /** Extra classes (e.g. dark-aware utility colors at a given site). */
  className?: string;
  /** Optional title tooltip (defaults to the aria-label). */
  title?: string;
}

const BASE_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  flex: '0 0 auto',
  padding: 0,
  borderRadius: 6,
  border: '1px solid var(--nb-border-2, #bcc3bc)',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  lineHeight: 1,
};

export default function CloseButton({
  onClick,
  ariaLabel = 'Close',
  size = 16,
  style,
  className,
  title,
}: CloseButtonProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      title={title ?? ariaLabel}
      onClick={onClick}
      className={className}
      style={{ ...BASE_STYLE, ...style }}
    >
      <X size={size} aria-hidden />
    </button>
  );
}
